import React, { useState } from 'react';
import { Card, Form, Input, Button, Select, Typography, message, Space, Divider, Tag, Popconfirm } from 'antd';
import { LockOutlined, BgColorsOutlined, CloudServerOutlined, KeyOutlined } from '@ant-design/icons';
import api from '../api/client';
import useAuthStore from '../store/authStore';
import useThemeStore, { ThemeMode } from '../store/themeStore';

const { Title, Text } = Typography;

const Profile: React.FC = () => {
  const [pwdForm] = Form.useForm();
  const [onecForm] = Form.useForm();
  const [pinForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [savingOnec, setSavingOnec] = useState(false);
  const [savingPin, setSavingPin] = useState(false);
  const user = useAuthStore((s) => s.user);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const themeMode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  const handleChangePassword = async (values: {
    current_password: string;
    new_password: string;
    new_password_confirm: string;
  }) => {
    if (values.new_password !== values.new_password_confirm) {
      message.error('Новые пароли не совпадают');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: values.current_password,
        new_password: values.new_password,
      });
      message.success('Пароль обновлён');
      pwdForm.resetFields();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось сменить пароль');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOnec = async (values: { onec_username: string; onec_password?: string }) => {
    setSavingOnec(true);
    try {
      await api.put('/auth/me/onec', {
        onec_username: values.onec_username,
        onec_password: values.onec_password || '',
      });
      message.success('1С-данные сохранены');
      onecForm.resetFields();
      await fetchMe();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось сохранить 1С-данные');
    } finally {
      setSavingOnec(false);
    }
  };

  const handleClearOnec = async () => {
    setSavingOnec(true);
    try {
      await api.delete('/auth/me/onec');
      message.success('1С-данные удалены');
      onecForm.resetFields();
      await fetchMe();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить 1С-данные');
    } finally {
      setSavingOnec(false);
    }
  };

  const handleSavePin = async (values: { pin_password: string; pin: string }) => {
    setSavingPin(true);
    try {
      await api.put('/auth/me/pin', {
        current_password: values.pin_password,
        pin: values.pin,
      });
      message.success('PIN обновлён');
      pinForm.resetFields();
      await fetchMe();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось сохранить PIN');
    } finally {
      setSavingPin(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <Title level={3}>Настройки</Title>
      <Text type="secondary">
        {user?.display_name || user?.username} ({user?.role})
      </Text>

      <Card
        title={<Space><BgColorsOutlined /> Тема оформления</Space>}
        style={{ marginTop: 24 }}
      >
        <Form layout="vertical">
          <Form.Item
            label="Выберите тему"
            help="Тема применяется сразу и сохраняется в браузере"
          >
            <Select<ThemeMode>
              value={themeMode}
              onChange={(v) => setMode(v)}
              style={{ maxWidth: 320 }}
              options={[
                { value: 'antd', label: 'Ant Design (классическая)' },
                { value: 'md3', label: 'Material 3 (Material You)' },
              ]}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={<Space><LockOutlined /> Смена пароля</Space>}
        style={{ marginTop: 24 }}
      >
        <Form
          form={pwdForm}
          layout="vertical"
          onFinish={handleChangePassword}
          autoComplete="off"
        >
          <Form.Item
            name="current_password"
            label="Текущий пароль"
            rules={[{ required: true, message: 'Введите текущий пароль' }]}
          >
            <Input.Password autoComplete="current-password" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="Новый пароль"
            rules={[
              { required: true, message: 'Введите новый пароль' },
              { min: 6, message: 'Минимум 6 символов' },
            ]}
          >
            <Input.Password autoComplete="new-password" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Form.Item
            name="new_password_confirm"
            label="Повтор нового пароля"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Повторите новый пароль' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Пароли не совпадают'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Divider style={{ margin: '12px 0' }} />
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={saving}>
              Сменить пароль
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={
          <Space>
            <KeyOutlined /> PIN для рабочего места
            {user?.has_pin ? <Tag color="green">задан</Tag> : <Tag>не задан</Tag>}
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          4-значный PIN для быстрого переключения оператора на общих устройствах
          (касса). PIN не заменяет пароль и работает только на рабочем месте.
        </Text>
        <Form
          form={pinForm}
          layout="vertical"
          onFinish={handleSavePin}
          autoComplete="off"
        >
          <Form.Item
            name="pin_password"
            label="Текущий пароль (подтверждение)"
            rules={[{ required: true, message: 'Введите текущий пароль' }]}
          >
            <Input.Password autoComplete="current-password" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Form.Item
            name="pin"
            label="Новый PIN (4 цифры)"
            rules={[
              { required: true, message: 'Введите PIN' },
              { pattern: /^\d{4}$/, message: 'Ровно 4 цифры' },
            ]}
          >
            <Input.Password maxLength={4} autoComplete="new-password" style={{ maxWidth: 160 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={savingPin}>
              Сохранить PIN
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={
          <Space>
            <CloudServerOutlined /> 1С настройки
            {user?.onec_configured
              ? <Tag color="green">заполнено</Tag>
              : <Tag color="red">не заполнено</Tag>}
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Логин и пароль вашей учётной записи 1С. Все операции с 1С выполняются от этого имени.
          Без них работа с 1С (заказ-наряды, склад, маркировка) будет недоступна.
        </Text>
        <Form
          form={onecForm}
          layout="vertical"
          onFinish={handleSaveOnec}
          autoComplete="off"
        >
          <Form.Item
            name="onec_username"
            label="Имя пользователя 1С"
            rules={[{ required: true, message: 'Введите имя пользователя 1С' }]}
          >
            <Input autoComplete="off" style={{ maxWidth: 320 }} placeholder="Например: Дмитрий" />
          </Form.Item>
          <Form.Item
            name="onec_password"
            label="Пароль 1С"
            help="Хранится в зашифрованном виде и никому не показывается"
          >
            <Input.Password autoComplete="new-password" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Divider style={{ margin: '12px 0' }} />
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={savingOnec}>
                Сохранить
              </Button>
              {user?.onec_configured && (
                <Popconfirm
                  title="Удалить 1С-данные?"
                  description="Работа с 1С станет недоступна до повторного заполнения"
                  onConfirm={handleClearOnec}
                  okText="Удалить"
                  cancelText="Отмена"
                >
                  <Button danger>Удалить</Button>
                </Popconfirm>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Profile;
